import { Injectable } from '@angular/core';
import { DeviceDetectorService } from 'ngx-device-detector';
import { v1 as uuidv1 } from 'uuid';
import { Platform } from '@ionic/angular/standalone';
import { buildEnv } from 'build-environment';

declare let DocumentTouch: any;

@Injectable({
  providedIn: 'root',
})
export class HelperFunctionsService {
  protected buildVersion: string = buildEnv.buildVersion;

  constructor(private deviceService: DeviceDetectorService, private platform: Platform) {}

  // A delay function that can be awaited
  // https://stackblitz.com/edit/typescript-wait?file=index.ts
  public async delay(ms): Promise<number> {
    return new Promise((resolve) =>
      setTimeout(() => {
        const val = Math.trunc(Math.random() * 100);
        resolve(val);
      }, ms)
    );
  }

  public localStorageAvailable(): boolean {
    // None service version in app.module.ts
    const test = uuidv1();
    try {
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch (e) {
      console.warn('Local storage is not available. All puzzle progress will be lost if you close this window');
      return false;
    }
  }

  public buildInfo(): string {
    return `Platforms: ${this.platform.platforms()}\nBuild-Version: ${this.buildVersion}`;
  }

  public isFirefox(): boolean {
    return this.deviceService.browser === 'Firefox';
  }

  public isApple(): boolean {
    return ['iOS', 'Mac'].indexOf(this.deviceService.os) !== -1;
  }

  public isIpad(): boolean {
    return this.isApple() && this.deviceService.isTablet();
  }

  public isIphone(): boolean {
    return this.isApple() && this.deviceService.isMobile();
  }

  public isIOS(): boolean {
    return this.isIpad() || this.isIphone();
  }

  public isInIframe(): boolean {
    return window.location !== window.parent.location;
  }

  public isAndroid(): boolean {
    return this.deviceService.os === 'Android';
  }

  /**
   * Returns true if it is a mobile or tablet device.
   */
  public isMobileDevice(): boolean {
    return this.deviceService.isMobile() || this.deviceService.isTablet();
  }

  /**
   * Check if we are on a mobile device with desktop mode enabled and return true if so.
   */
  public isMobileDesktopMode(): boolean {
    // console.log('vendor:', navigator.vendor);
    // console.log('userAgent:', navigator.userAgent);
    // console.log('platform:', navigator.platform);
    // console.log('appVersion:', navigator.appVersion);
    // console.log('isMobileDevice():', this.isMobileDevice());

    // **************
    // Chrome, Opera, Brave, and Samsung Internet on Android
    // **************
    // console.log('Chrome');
    // From https://stackoverflow.com/a/41797765/4396362
    // Seems to work fine
    const webkitVer = parseInt(/WebKit\/([0-9]+)|$/.exec(navigator.appVersion)[1], 10); // also matches AppleWebKit
    const isGoogle = webkitVer && navigator.vendor.indexOf('Google') === 0; // Also true for Opera Mobile and maybe others
    const isAndroid = isGoogle && navigator.userAgent.indexOf('Android') > 0; // Careful - Firefox and Windows Mobile also have Android in user agent
    const androidChromeDesktopMode =
      !isAndroid &&
      isGoogle &&
      navigator.platform.indexOf('Linux a') === 0 &&
      'ontouchstart' in document.documentElement;

    // console.log('webkitVer:', webkitVer);
    // console.log('isGoogle:', isGoogle);
    // console.log('isAndroid:', isAndroid);
    // console.log('androidChromeDesktopMode:', androidChromeDesktopMode);
    // console.log("androidChromeDesktopMode", androidChromeDesktopMode);
    // console.log(this.deviceService.isMobile(), this.deviceService.isTablet(), this.deviceService.isDesktop(), this.deviceService.getDeviceInfo());

    // **************
    // Firefox on Android
    // **************
    // console.log('Firefox');
    const isAndroidFF = navigator.appVersion.indexOf('Android') > 0;
    const isFF = navigator.userAgent.indexOf('Firefox') > 0;
    const androidFFDesktopMode = isAndroidFF && isFF && !this.isMobileDevice();
    // console.log('isAndroidFF:', isAndroidFF);
    // console.log('isFF:', isFF);
    // console.log('androidFFDesktopMode:', androidFFDesktopMode);

    // **************
    // iOS
    // **************
    // console.log('iOS');
    // From https://stackoverflow.com/a/52538691/4396362
    // Original didn't work, but seems to work now.
    const iOSAgent = navigator.userAgent.match(/iPhone|iPod|iPad/);
    const iOSPlatform = navigator.platform && navigator.platform.match(/iPhone|iPod|iPad/);
    // const iOSDesktopMode = (!iOSAgent && iOSPlatform);
    const iOSDesktopMode = (!iOSAgent || !iOSPlatform) && this.isMobileDevice() && this.isApple();

    // console.log('mobile', this.isMobileDevice());
    // console.log('iOS agent', iOSAgent);
    // console.log('iOS platform', iOSPlatform);
    // console.log('iOS desktop', iOSDesktopMode);

    return androidChromeDesktopMode || iOSDesktopMode || androidFFDesktopMode;

    // From https://stackoverflow.com/a/76012295/4396362
    // Does not work
    // return window.innerWidth > screen.availWidth;
  }

  public unsupportedBrowser(puzzleKeyboardInput = false, nativeKeyboard = false): boolean {
    const unSupportedBrowsers = ['IE'];
    return (
      unSupportedBrowsers.indexOf(this.deviceService.browser) !== -1 ||
      (puzzleKeyboardInput && nativeKeyboard && this.isFirefox() && this.isAndroid())
    );
  }

  public hasTouchScreen(): boolean {
    let result = false;
    if ((window as any).PointerEvent && 'maxTouchPoints' in navigator) {
      // if Pointer Events are supported, just check maxTouchPoints
      if (navigator.maxTouchPoints > 0) {
        result = true;
      }
    } else {
      // no Pointer Events...
      if (window.matchMedia && window.matchMedia('(any-pointer:coarse)').matches) {
        // check for any-pointer:coarse which mostly means touchscreen
        result = true;
      } else if ((window as any).TouchEvent || 'ontouchstart' in window) {
        // last resort - check for exposed touch events API / event handler
        result = true;
      }
    }
    return result;
  }

  public getAllIndexes(arr: any[], val: any): number[] {
    const indexes = [];
    let i = -1;
    while ((i = arr.indexOf(val, i + 1)) !== -1) {
      indexes.push(i);
    }
    return indexes;
  }

  // public getKeyboardHeight() {
  //   let height = 0;
  //   if (this.isIpad()) {
  //     // Keyboard size on portrait or landscape.
  //     height = window.innerHeight > window.innerWidth ? 350 : 440;
  //   } else if (this.isIphone()) {
  //     // Keyboard size on portrait or landscape.
  //     height = window.innerHeight > window.innerWidth ? 216 : 162;
  //   }
  //   return height;
  // }

  public async timeout(ms = 0) {
    return new Promise((resolve) => window.setTimeout(resolve, ms));
  }

  public randomizeArray(array: any) {
    let currentIndex = array.length;
    let temporaryValue;
    let randomIndex;

    // While there remain elements to shuffle...
    while (0 !== currentIndex) {
      // Pick a remaining element...
      randomIndex = Math.floor(Math.random() * currentIndex);
      currentIndex -= 1;

      // And swap it with the current element.
      temporaryValue = array[currentIndex];
      array[currentIndex] = array[randomIndex];
      array[randomIndex] = temporaryValue;
    }
    return array;
  }
}

export function debounce(delay: number = 300): MethodDecorator {
  return function (target: any, propertyKey: string, descriptor: PropertyDescriptor) {
    const timeoutKey = Symbol();

    const original = descriptor.value;

    descriptor.value = function (...args) {
      clearTimeout(this[timeoutKey]);
      this[timeoutKey] = setTimeout(() => original.apply(this, args), delay);
    };

    return descriptor;
  };
}

/*
 * Used for identifying objects that are instantiated more than once.  Call it
 * i.e. in the constructor on `this` and again on an instance outside, and
 * output the result to identify multiple instances where there should be only
 * one.
 */

const objIdMap = new WeakMap();
let objectCount = 0;
export function objectId(object) {
  if (!objIdMap.has(object)) {
    objIdMap.set(object, ++objectCount);
  }
  return objIdMap.get(object);
}
